import { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { Typography } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached';
import SpeedIcon from '@mui/icons-material/Speed';
import PaperStyled from '../PaperStyled';
import AvailableSpeedOptions from './AvailableOptions/AvailableSpeedOptions';
import StyledTextfield from './UI/StyledTextfield';
import StyledSelect from './UI/StyledSelect';
import MyLabel from './UI/MyLabel';
import MyIcon from './UI/MyIcon';
import TransferUnits from './AvailableOptions/TransferUnits';
import StyledRecordDuration from './UI/StyledRecordDuration';
import TransferTimeCalculations from '../Helper/TransferTimeCalculations';
import AvailableModes from './AvailableOptions/AvailableModes';
import StyledButton from './UI/StyledButton';
import { Checkbox, FormControlLabel } from '@mui/material';
import StorageSize from './UI/StorageSize';
import AvailableStorageUnits from './AvailableOptions/AvailableStorageUnits';
import ShareButtons from './UI/ShareButtons';
import { GetTransferTimeStringToShareInformation } from '../Helper/GetStringToShareInformation';
import {
  getInterfaceTypeKeyFromLocalStorage,
  saveInterfaceTypeToLocalStorage,
  getSpeedUnitsFromLocalStorage,
  saveSpeedUnitsToLocalStorage,
  getModeTransferTimeFromLocalStorage,
  saveModeTransferTimeToLocalStorage,
  getAvailableModesOptionsTransferTimeFromLocalStorage,
  saveAvailableModesOptionsTransferTimeToLocalStorage,
  getUseCustomStorageSizeKeyFromLocalStorage,
  saveUseCustomStorageSizeKeyToLocalStorage,
  getCustomStorageSizeKeyFromLocalStorage,
  saveCustomStorageSizeKeyToLocalStorage,
  getCustomStorageSizeUnitsKeyFromLocalStorage,
  saveCustomStorageSizeUnitsKeyToLocalStorage
} from '../Utils/LocalStorageHelper';

const SpeedCalculator = ({ storageResult, storageUnits }) => {
  const [interfaceType, setInterfaceType] = useState(getInterfaceTypeKeyFromLocalStorage(AvailableSpeedOptions[0]));
  const [speedUnits, setSpeedUnits] = useState(getSpeedUnitsFromLocalStorage(TransferUnits[0]));
  const [modeTransferTime, setModeTransferTime] = useState(getModeTransferTimeFromLocalStorage(AvailableModes[1]));
  const [availableModesOptionsTransferTime, setAvailableModesOptionsTransferTime] = useState(
    getAvailableModesOptionsTransferTimeFromLocalStorage(AvailableModes)
  );
  const [transferTimeResult, setTransferTimeResult] = useState({ days: 0, hours: 0, minutes: 0 });
  const [useCustomStorageSize, setUseCustomStorageSize] = useState(getUseCustomStorageSizeKeyFromLocalStorage(true));
  const [customStorageSize, setCustomStorageSize] = useState(getCustomStorageSizeKeyFromLocalStorage(0));
  const [customStorageSizeUnits, setCustomStorageSizeUnits] = useState(getCustomStorageSizeUnitsKeyFromLocalStorage(AvailableStorageUnits[2]));

  const updateInterfaceTypeValue = (value) => {
    setInterfaceType(value);
    saveInterfaceTypeToLocalStorage(value);
  };

  const updateSpeedUnitsValue = (value) => {
    setSpeedUnits(value);
    saveSpeedUnitsToLocalStorage(value);
  };

  const updateModeTransferTime = (value) => {
    setModeTransferTime(value);
    saveModeTransferTimeToLocalStorage(value);
  };

  const updateAvailableModesOptionsTransferTime = (value) => {
    setAvailableModesOptionsTransferTime(value);
    saveAvailableModesOptionsTransferTimeToLocalStorage(value);
  };

  const updateUseCustomStorageSize = (value) => {
    setUseCustomStorageSize(value);
    saveUseCustomStorageSizeKeyToLocalStorage(value);
  };

  const updateCustomStorageSize = (value) => {
    setCustomStorageSize(value);
    saveCustomStorageSizeKeyToLocalStorage(value);
  };

  const updateCustomStorageUnits = (value) => {
    setCustomStorageSizeUnits(value);
    saveCustomStorageSizeUnitsKeyToLocalStorage(value);
  };

  const resetSettings = () => {
    updateInterfaceTypeValue(AvailableSpeedOptions[0]);
    updateSpeedUnitsValue(TransferUnits[0]);
    updateModeTransferTime(AvailableModes[1]);
    updateAvailableModesOptionsTransferTime(AvailableModes);
    updateCustomStorageSize(0);
    updateCustomStorageUnits(AvailableStorageUnits[2]);
  };

  useEffect(() => {
    let foundedValue = TransferUnits.find((unit) => {
      return unit.label === interfaceType.units;
    });
    if (foundedValue) {
      updateSpeedUnitsValue(foundedValue);
    } else {
      updateSpeedUnitsValue(TransferUnits[0]);
    }
  }, [interfaceType]);

  useEffect(() => {
    let result = 0;
    result = TransferTimeCalculations({
      storageResult: customStorageSize,
      storageUnits: customStorageSizeUnits,
      interfaceType,
      speedUnits
    });

    setTransferTimeResult(result);
  }, [interfaceType, speedUnits, storageResult, storageUnits, modeTransferTime, useCustomStorageSize, customStorageSize, customStorageSizeUnits]);

  useEffect(() => {
    if (!useCustomStorageSize) {
      updateCustomStorageSize(storageResult);
      updateCustomStorageUnits(storageUnits);
    }
  }, [useCustomStorageSize, storageResult, storageUnits]);

  let informationToShare = GetTransferTimeStringToShareInformation({
    storage: useCustomStorageSize ? customStorageSize : storageResult,
    storageUnits: useCustomStorageSize ? customStorageSizeUnits : storageUnits,
    interfaceType: interfaceType,
    speed: interfaceType.speed,
    speedUnits: speedUnits,
    transferTimeResult: transferTimeResult
  });

  return (
    <Grid container paddingLeft={3} paddingRight={3} spacing={2} paddingBottom={'20px'}>
      <Grid item xs={6}>
        <Typography component="div" variant="h6" color="white" textAlign="left" sx={{ fontWeight: 'bold' }}>
          Transfer Time
        </Typography>
      </Grid>
      <Grid item xs={6} style={{ textAlign: 'right', marginTop: '10px' }}>
        <ShareButtons subject={'Transfer Time'} informationToShare={informationToShare} />
      </Grid>
      <Grid
        item
        md={4}
        xs={12}
        container
        direction="row"
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'center',
          height: '100px'
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              sx={{
                color: 'white'
              }}
              checked={useCustomStorageSize}
              onChange={(event) => {
                updateUseCustomStorageSize(event.target.checked);
              }}
            />
          }
          label={<div style={{ color: 'white' }}>Use custom storage size</div>}
        />
      </Grid>

      <StorageSize
        id={'custom-storage-size'}
        storageResult={customStorageSize}
        setStorageResult={updateCustomStorageSize}
        isDisabled={useCustomStorageSize ? false : true}
        isDisabledUnits={useCustomStorageSize ? false : true}
        storageUnits={customStorageSizeUnits}
        setStorageUnits={updateCustomStorageUnits}
        disabledcolor="lightgrew"
        md={4}
        xs={6}
      />

      <Grid item md={3} xs={4}>
        <PaperStyled>
          <MyLabel logo={''} label="Preset speed" />
          <StyledSelect
            id="interface-type"
            labelId="interface-type-label"
            value={interfaceType}
            setValue={updateInterfaceTypeValue}
            optionsList={AvailableSpeedOptions}
          />
        </PaperStyled>
      </Grid>
      <Grid item md={2} xs={4}>
        <PaperStyled>
          <MyIcon Icon={SpeedIcon} label="Speed" />
          <StyledTextfield
            id="transf-speed"
            type="number"
            value={interfaceType.value}
            minValue={0}
            setValue={(value) => {
              let foundedValue = AvailableSpeedOptions.find((item) => {
                return item.value === value;
              });

              if (foundedValue) {
                updateInterfaceTypeValue(foundedValue);
              } else {
                updateInterfaceTypeValue({ value: value, units: speedUnits.label, label: 'custom' });
              }
            }}
          />
        </PaperStyled>
      </Grid>
      <Grid item md={2} xs={4}>
        <PaperStyled>
          <MyLabel logo={''} label="Units" />
          <StyledSelect
            id="net-speed"
            labelId="net-speed-label"
            value={speedUnits}
            setValue={(value) => {
              updateInterfaceTypeValue({ value: interfaceType.value, units: value.label, label: 'custom' });
              updateSpeedUnitsValue(value);
            }}
            optionsList={TransferUnits}
          />
        </PaperStyled>
      </Grid>
      <Grid
        item
        md={5}
        xs={12}
        container
        direction="row"
        sx={{
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <PaperStyled>
          <MyIcon Icon={CachedIcon} label="Transfer Time" marginLeft="40px" />
          <StyledRecordDuration
            time={transferTimeResult}
            mode={modeTransferTime}
            setMode={updateModeTransferTime}
            availableModesOptions={availableModesOptionsTransferTime}
            setAvailableModesOptions={updateAvailableModesOptionsTransferTime}
            isDisabled={true}
          />
        </PaperStyled>
      </Grid>
      <Grid item md={12} xs={12}>
        <PaperStyled>
          <StyledButton onClickHandler={resetSettings} label={'Clear Transfer Time Calculator'} height="30px" width="100%" />
        </PaperStyled>
      </Grid>
    </Grid>
  );
};

export default SpeedCalculator;
